/**
 * PER-COURSE SETTINGS panel (course_settings) — AUTONOMY · Layer 1.
 *
 * Closes the "complete backend, zero UI" gap: the table and the write-path
 * (author/editor authz) existed, but `upsertCourseSettingsMutation` had no
 * caller. Exposes, registry-driven and self-describing (Layer 2):
 *  · enabled module kinds (editor's "add module" menu),
 *  · course grading scale (org→course→cohort override),
 *  · course's default live-class provider,
 *  · video size limit (media_settings.maxVideoFileBytes).
 */
import { createEffect, createSignal, For, Show } from "solid-js";
import { msg } from "@lingui/core/macro";
import { Settings2 } from "@fleet/icons";

import {
  assignmentModuleManifest,
  baseMeetingProviders,
  courseConfigurableFeatures,
  courseModuleManifests,
  dependentsOf,
  featureGraph,
  modelShapeFromRow,
  modelStrategy,
  parseFeatureOverrides,
  parseRequireClause,
  resolveFeatureEffective,
  upsertCourseSettingsMutation,
  validateCourseOverridesChange,
} from "@space/shared";
// FLAGS: tri-state features per COURSE + confirmation dialog with
// implications and graph validation (baseline = platform ⊕ active org).
import {
  FeatureToggleDialog,
  type FeatureToggleRequest,
} from "@/features/flags/feature-toggle-dialog";
import { usePlatformFlags } from "@/features/flags/use-feature-enabled";
import { useActiveOrg } from "@/features/academy/hooks/use-active-org";
import { useGradingModels, useOrgSettings } from "@/features/academy/hooks/use-academy-data";
import { useI18n } from "@/providers/i18n-provider";
import { Select } from "@/components/ui/select";
import { Switch, TextField } from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { showToast } from "@/components/ui/toast";
import { InfoHint } from "@/components/ui/info-hint";
import { useCourseSettings } from "@/features/academy/hooks/use-course-modules";
import { useCourseWrite } from "@/features/courses/hooks/use-course-data";
import { moduleRegistry } from "@/features/academy/modules/registry";

const INHERIT = "__inherit__";
const BYTES_PER_MB = 1024 * 1024;

/** Description of a kind from its manifest (`academy.module.<kind>`). */
function moduleKindDescription(kind: string): string {
  const manifests = [...courseModuleManifests(), assignmentModuleManifest()];
  return manifests.find((m) => m.key === `academy.module.${kind}`)?.description ?? "";
}

function parseEnabledKinds(raw: string | null | undefined, allKinds: string[]): Set<string> {
  if (!raw) {
    return new Set(allKinds);
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      return new Set(allKinds);
    }
    return new Set(parsed.filter((k): k is string => typeof k === "string"));
  } catch {
    return new Set(allKinds);
  }
}

function parseMediaSettings(raw: string | null | undefined): Record<string, unknown> {
  if (!raw) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return {};
    }
    // Field-by-field copy — avoids the unsafe type assertion over `unknown`.
    return Object.fromEntries(Object.entries(parsed));
  } catch {
    return {};
  }
}

export function CourseSettingsPanel(props: { courseId: string }) {
  const { t } = useI18n();
  const settings = useCourseSettings(() => props.courseId);
  const { drizzle } = useCourseWrite();

  const allKinds = moduleRegistry.kinds();

  const [enabledKinds, setEnabledKinds] = createSignal<Set<string>>(new Set(allKinds));
  const [scheme, setScheme] = createSignal(INHERIT);
  const [provider, setProvider] = createSignal(INHERIT);
  const [maxVideoMb, setMaxVideoMb] = createSignal("");
  const [saving, setSaving] = createSignal(false);
  // FLAGS: course feature overrides (tri-state) + pending dialog.
  const [featureOverrides, setFeatureOverrides] = createSignal<Record<string, boolean>>({});
  const [pendingChange, setPendingChange] = createSignal<
    (FeatureToggleRequest & { choice: "default" | "on" | "off" }) | null
  >(null);
  const platformFlags = usePlatformFlags();
  const { activeOrgId } = useActiveOrg();
  const orgSettings = useOrgSettings(() => activeOrgId());
  // Group E: the course scale is chosen among the org's MODELS (sync).
  const gradingModels = useGradingModels(() => activeOrgId());
  const modelSummary = (m: {
    kind: string | null;
    max_value: number | null;
    passing_threshold: number | null;
    decimals: number | null;
    letter_bands: unknown;
    display_suffix: string | null;
    system_key: string | null;
  }): string => {
    const shape = modelShapeFromRow({
      id: "x",
      kind: m.kind ?? "points",
      max_value: m.max_value ?? 0,
      passing_threshold: m.passing_threshold ?? 0,
      decimals: m.decimals ?? 0,
      letter_bands: m.letter_bands,
      display_suffix: m.display_suffix,
      system_key: m.system_key,
    });
    const strat = modelStrategy(shape);
    return `${strat.displayValue(shape.passingThreshold * shape.maxValue)} / ${strat.displayValue(shape.maxValue)}`;
  };
  const graph = featureGraph();
  const courseFeatures = courseConfigurableFeatures();

  createEffect(() => {
    const s = settings.data;
    setFeatureOverrides({ ...parseFeatureOverrides(s?.features) });
    setEnabledKinds(parseEnabledKinds(s?.enabled_module_kinds, allKinds));
    setScheme(s?.grading_model_id ?? INHERIT);
    setProvider(s?.meeting_provider ?? INHERIT);
    const media = parseMediaSettings(s?.media_settings);
    const bytes = media.maxVideoFileBytes;
    setMaxVideoMb(typeof bytes === "number" ? String(Math.round(bytes / BYTES_PER_MB)) : "");
  });

  const toggleKind = (kind: string, on: boolean) => {
    setEnabledKinds((prev) => {
      const next = new Set(prev);
      if (on) {
        next.add(kind);
      } else {
        next.delete(kind);
      }
      return next;
    });
  };

  // ── FLAGS: course feature tri-state with confirmation ────────────────────
  type OverrideChoice = "default" | "on" | "off";
  const overrideChoice = (key: string): OverrideChoice => {
    const value = featureOverrides()[key];
    return value === undefined ? "default" : value ? "on" : "off";
  };
  const applyOverrideChoice = (key: string, choice: OverrideChoice) => {
    setFeatureOverrides((prev) => {
      const next = { ...prev };
      if (choice === "default") {
        delete next[key];
      } else {
        next[key] = choice === "on";
      }
      return next;
    });
  };
  const requestOverrideChoice = (key: string, choice: OverrideChoice) => {
    if (choice === overrideChoice(key)) {
      return;
    }
    const orgOverrides = parseFeatureOverrides(orgSettings.data?.features);
    const prev = featureOverrides();
    const next = { ...prev };
    if (choice === "default") {
      delete next[key];
    } else {
      next[key] = choice === "on";
    }
    const violations = validateCourseOverridesChange({
      platform: platformFlags(),
      org: orgOverrides,
      oldCourse: prev,
      newCourse: next,
    });
    const feature = courseFeatures.find((f) => f.key === key);
    const nextEnabled = resolveFeatureEffective(key, {
      platformMap: platformFlags(),
      org: orgOverrides,
      course: next,
    }).enabled;
    const softAffected = nextEnabled
      ? []
      : dependentsOf(key, graph)
          .filter((n) =>
            n.requires.some((clause) =>
              parseRequireClause(clause).some((a) => a.key === key && a.soft),
            ),
          )
          .map((n) => n.name);
    setPendingChange({
      choice,
      key,
      name: feature?.name ?? key,
      nextEnabled,
      status: feature?.status,
      since: feature?.since,
      implications: feature?.implications,
      violations,
      softAffected,
    });
  };

  const handleSave = async () => {
    const db = drizzle();
    if (!db) {
      return;
    }
    setSaving(true);
    try {
      const kinds = enabledKinds();
      const media = parseMediaSettings(settings.data?.media_settings);
      const mb = Number(maxVideoMb());
      if (maxVideoMb().trim() !== "" && Number.isFinite(mb) && mb > 0) {
        media.maxVideoFileBytes = Math.round(mb * BYTES_PER_MB);
      } else {
        delete media.maxVideoFileBytes;
      }
      await upsertCourseSettingsMutation(db, props.courseId, {
        // All kinds ⇒ null (inherits "everything registered", future ones included).
        enabled_module_kinds: kinds.size === allKinds.length ? null : [...kinds],
        // Group E: the chosen model is persisted; the legacy key is cleared
        // (the model supersedes it in resolution).
        grading_model_id: scheme() === INHERIT ? null : scheme(),
        grading_scheme: null,
        meeting_provider: provider() === INHERIT ? null : provider(),
        media_settings: media,
        // FLAGS: course's tri-state overrides (the write-path validates the graph).
        features: featureOverrides(),
      });
      showToast(t(msg`Course settings saved`), "success");
    } catch (err) {
      console.error("Error saving course settings:", err);
      showToast(t(msg`Error saving settings`), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      class="space-y-4 rounded-2xl border border-border bg-surface/40 p-4"
      data-testid="course-settings-panel"
    >
      <div class="flex items-center gap-2">
        <Settings2 class="h-5 w-5 text-primary" />
        <h2 class="text-lg font-bold">{t(msg`Course settings`)}</h2>
      </div>
      {/* Layer 2: visible purpose. */}
      <p class="text-sm text-muted-foreground">
        {t(
          msg`These settings apply to every cohort of this course: which module types authors can add, the grading scale, the default live-class provider and media limits. Cohorts can override the scale and the provider.`,
        )}
      </p>

      {/* Enabled module kinds (registry-driven, with descriptions). */}
      <div class="space-y-2">
        <h3 class="text-sm font-bold">
          {t(msg`Enabled module types`)}{" "}
          <InfoHint>
            {t(
              msg`Disabling a type hides it from the "add module" menu of this course. Existing modules of that type are kept.`,
            )}
          </InfoHint>
        </h3>
        <div class="grid gap-2 sm:grid-cols-2" data-testid="course-module-kind-toggles">
          <For each={allKinds}>
            {(kind) => (
              <label class="flex items-center justify-between gap-3 rounded-lg border border-border/40 px-3 py-2">
                <span class="min-w-0">
                  <span class="block text-sm font-medium capitalize">{kind.replace("_", " ")}</span>
                  <span
                    class="block truncate text-xs text-muted-foreground"
                    title={moduleKindDescription(kind)}
                  >
                    {moduleKindDescription(kind)}
                  </span>
                </span>
                <Switch
                  checked={enabledKinds().has(kind)}
                  onCheckedChange={(on) => toggleKind(kind, on)}
                  data-testid={`module-kind-toggle-${kind}`}
                />
              </label>
            )}
          </For>
        </div>
      </div>

      {/* ── FLAGS: course feature overrides (tri-state) ── */}
      <div class="space-y-2">
        <h3 class="text-sm font-bold">
          {t(msg`Feature overrides`)}{" "}
          <InfoHint>
            {t(
              msg`Refine per-course what your academy enables. "Inherit" follows the academy setting; existing content of a disabled feature is preserved and shown as a disabled stop.`,
            )}
          </InfoHint>
        </h3>
        <div class="space-y-2" data-testid="course-feature-overrides">
          <For each={courseFeatures}>
            {(feature) => (
              <div
                class="flex items-center justify-between gap-3 rounded-lg border border-border/40 px-3 py-2"
                data-testid={`course-feature-row-${feature.key}`}
              >
                <div class="min-w-0">
                  <p class="truncate text-sm font-medium">{feature.name}</p>
                  <p class="truncate text-xs text-muted-foreground" title={feature.description}>
                    {feature.description}
                  </p>
                </div>
                <Select
                  aria-label={feature.name}
                  value={overrideChoice(feature.key)}
                  onChange={(v) => {
                    requestOverrideChoice(feature.key, v === "on" || v === "off" ? v : "default");
                  }}
                  options={[
                    { value: "default", label: t(msg`Inherit`) },
                    { value: "on", label: t(msg`Enabled`) },
                    { value: "off", label: t(msg`Disabled`) },
                  ]}
                />
              </div>
            )}
          </For>
        </div>
      </div>

      <div class="grid gap-4 sm:grid-cols-3">
        <label class="space-y-1.5">
          <span class="text-sm font-medium">
            {t(msg`Grading scale`)}{" "}
            <InfoHint>
              {t(
                msg`Overrides the academy default for this course; cohorts can override it again. Sealed grades never change retroactively.`,
              )}
            </InfoHint>
          </span>
          <Select
            aria-label={t(msg`Grading scale`)}
            value={scheme()}
            onChange={setScheme}
            options={[
              { value: INHERIT, label: t(msg`Inherit from academy`) },
              ...(gradingModels.data ?? []).map((m) => ({
                value: m.id,
                label: `${m.name ?? ""} (${modelSummary(m)})`,
              })),
            ]}
          />
        </label>
        <label class="space-y-1.5">
          <span class="text-sm font-medium">
            {t(msg`Live class provider`)}{" "}
            <InfoHint>
              {t(
                msg`Default provider for this course's live sessions. Each cohort or session can still override it.`,
              )}
            </InfoHint>
          </span>
          <Select
            aria-label={t(msg`Live class provider`)}
            value={provider()}
            onChange={setProvider}
            options={[
              { value: INHERIT, label: t(msg`Inherit (cohort decides)`) },
              ...baseMeetingProviders.map((p) => ({
                value: p.manifest.id,
                label: p.manifest.name,
              })),
            ]}
          />
        </label>
        <label class="space-y-1.5">
          <span class="text-sm font-medium">
            {t(msg`Max video size (MB)`)}{" "}
            <InfoHint>
              {t(msg`Upload limit for videos in this course. Empty inherits the platform limit.`)}
            </InfoHint>
          </span>
          <TextField
            type="number"
            inputmode="numeric"
            placeholder={t(msg`Inherit`)}
            value={maxVideoMb()}
            onInput={(e) => setMaxVideoMb(e.currentTarget.value)}
          />
        </label>
      </div>

      <Button
        onClick={() => {
          void handleSave();
        }}
        disabled={saving()}
        data-testid="course-settings-save"
      >
        <Show when={saving()} fallback={t(msg`Save course settings`)}>
          {t(msg`Saving...`)}
        </Show>
      </Button>
      {/* FLAGS: confirmation for each toggle with implications + graph. */}
      <FeatureToggleDialog
        request={pendingChange()}
        graph={graph}
        onConfirm={() => {
          const change = pendingChange();
          setPendingChange(null);
          if (change) {
            applyOverrideChoice(change.key, change.choice);
          }
        }}
        onCancel={() => setPendingChange(null)}
      />
    </section>
  );
}
